import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Zap } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import EntriesTable from "./EntriesTable";

const PA_META = {
  ambos: { label: "PA Ambos times", color: "#F59E0B" },
  casa: { label: "PA Casa", color: "#22D3EE" },
  fora: { label: "PA Fora", color: "#EC4899" },
};

function formatBRL(n) {
  const sign = n < 0 ? "-" : n > 0 ? "+" : "";
  return `${sign}R$ ${Math.abs(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function PAPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePa, setActivePa] = useState("all"); // "all" | "ambos" | "casa" | "fora"
  const [matchFilter, setMatchFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      // Only pending games — entries with PA that have NOT been finalized yet
      const res = await api.get("/entries", { params: { early_payout: true, finalized: false } });
      setEntries(res.data);
    } catch {
      toast.error("Erro ao carregar operações com PA");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Entrada excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (activePa !== "all" && e.pa_type !== activePa) return false;
      if (matchFilter !== "all") {
        const label = e.home_team && e.away_team
          ? `${e.home_team} x ${e.away_team}`
          : (e.match || "");
        if (label !== matchFilter) return false;
      }
      return true;
    });
  }, [entries, activePa, matchFilter]);

  // Distinct match labels from entries
  const matchOptions = useMemo(() => {
    const set = new Set();
    for (const e of entries) {
      const label = e.home_team && e.away_team
        ? `${e.home_team} x ${e.away_team}`
        : (e.match || "");
      if (label) set.add(label);
    }
    return Array.from(set).sort();
  }, [entries]);

  const totalProfit = filtered.reduce((s, e) => s + (e.profit || 0), 0);
  const totalRevenue = filtered.reduce((s, e) => s + (e.revenue || 0), 0);
  const wins = filtered.filter((e) => e.profit > 0).length;
  const winRate = filtered.length ? Math.round((wins / filtered.length) * 1000) / 10 : 0;

  const countBy = { ambos: 0, casa: 0, fora: 0 };
  for (const e of entries) if (e.pa_type in countBy) countBy[e.pa_type]++;

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5 md:gap-4">
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[0.75rem] uppercase tracking-widest text-zinc-500">Total operações</span>
            <Zap className="h-4 w-4 text-amber-400" />
          </div>
          <div data-testid="pa-total" className="font-mono-num text-3xl font-bold text-white">
            {entries.length}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">
            Receita no filtro
          </div>
          <div
            data-testid="pa-total-revenue"
            className="font-mono-num text-2xl font-bold text-zinc-100"
          >
            {formatBRL(totalRevenue)}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">
            Lucro no filtro
          </div>
          <div
            className="font-mono-num text-2xl font-bold"
            style={{ color: totalProfit >= 0 ? "#00FF66" : "#FF3B30" }}
          >
            {formatBRL(totalProfit)}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">Taxa de acerto</div>
          <div className="font-mono-num text-2xl font-bold text-blue-400">{winRate.toFixed(1)}%</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 text-[0.75rem] uppercase tracking-widest text-zinc-500">Por tipo</div>
          <div className="space-y-1 text-xs">
            {Object.entries(countBy).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-zinc-400">{PA_META[k].label}</span>
                <span className="font-mono-num" style={{ color: PA_META[k].color }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-2xl font-bold text-white">Pagamento antecipado</h2>
        <p className="text-xs text-zinc-500">
          Apenas partidas que ainda vão acontecer — marque como finalizada no Histórico para removê-las daqui.
        </p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { v: "all", label: `Todos (${entries.length})`, color: "#D4FF00" },
          { v: "ambos", label: `PA Ambos (${countBy.ambos})`, color: PA_META.ambos.color },
          { v: "casa", label: `PA Casa (${countBy.casa})`, color: PA_META.casa.color },
          { v: "fora", label: `PA Fora (${countBy.fora})`, color: PA_META.fora.color },
        ].map((o) => {
          const active = activePa === o.v;
          return (
            <Button
              key={o.v}
              data-testid={`pa-filter-${o.v}`}
              variant="ghost"
              onClick={() => setActivePa(o.v)}
              className="h-9 rounded-full border px-4 text-xs font-semibold"
              style={{
                borderColor: active ? o.color : "rgba(255,255,255,0.1)",
                backgroundColor: active ? `${o.color}20` : "transparent",
                color: active ? o.color : "#D4D4D8",
                transition: "border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease",
              }}
            >
              {o.label}
            </Button>
          );
        })}
        <span className="mx-1 h-6 w-px bg-white/10" />
        <Select value={matchFilter} onValueChange={setMatchFilter}>
          <SelectTrigger
            data-testid="pa-match-filter"
            className="h-9 w-[240px] border-white/10 bg-[#121212] text-sm"
          >
            <SelectValue placeholder="Todos confrontos" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px] border-white/10 bg-[#121212] text-zinc-200">
            <SelectItem value="all">Todos confrontos</SelectItem>
            {matchOptions.map((m) => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {matchFilter !== "all" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMatchFilter("all")}
            className="text-xs text-zinc-500 hover:bg-white/5 hover:text-white"
          >
            Limpar
          </Button>
        )}
      </div>

      <EntriesTable
        entries={filtered}
        onEdit={() => {}}
        onDelete={handleDelete}
        showFinalized
        onToggleFinalized={async (id, val) => {
          try {
            await api.patch(`/entries/${id}/finalize`, { finalized: val });
            // Refresh — finalized entries drop out of PA tab
            load();
          } catch {
            toast.error("Erro ao atualizar status");
          }
        }}
      />
    </div>
  );
}
